// ------------------------------------------------------------------------------
// <copyright file="retryStopSession.js" company="C2HL">
//     Copyright (c) 2024 C2HL. All rights reserved.
// </copyright>
// ------------------------------------------------------------------------------

const DELAYS_MS = [500, 1500];
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Sends the host's stop-session request up to 3 times (#4907). Only a 5xx answer, a network error or an
 * attempt with no answer within `timeoutMs` (aborted through the signal handed to `send`) is retried; a 4xx
 * is the server's verdict and is returned as-is. Example: 503 → wait 500 ms → 200 returns the 200; 409
 * returns at once. The last 5xx is returned and the last error is thrown.
 * @param {(signal: AbortSignal) => Promise<Response>} send performs one stop-session request
 * @param {{ sleep?: (ms: number) => Promise<void>, timeoutMs?: number }} [options]
 * @returns {Promise<Response>}
 */
export async function retryStopSession(send, { sleep = wait, timeoutMs = 8000 } = {}) {
  for (let attempt = 0; ; attempt += 1) {
    const last = attempt >= DELAYS_MS.length;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const response = await send(controller.signal);
      if (response.status < 500 || last) return response;
    } catch (error) {
      if (last) throw error;
    } finally {
      clearTimeout(timer);
    }
    await sleep(DELAYS_MS[attempt]);
  }
}
